import React from "react";
import * as Styled from "./styles";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import Tooltip from "@components/common/Tooltip";
import {
  Box,
  Flex,
  Icon,
  Portal,
  Text,
  useColorModeValue,
  useRadio,
} from "@chakra-ui/react";
import { EqualInputSelect } from "@hook/useEqualCode";
import { theme } from "src/styles/theme";

interface Option {
  id?: any;
  idRelacionamentoComprador?: any;
  name?: any;
  nome?: any;
}

interface ISelect {
  isMulti?: boolean;
  title: string;
  label: string | string[];
  error?: boolean;
  isSelected?: string | string[];
  height?: string;
  widht?: string;
  widhtMobile?: string;
  disabled?: boolean;
  isRequirement?: boolean;
  isOportunity?: boolean;
  readOnly?: boolean;

  options: Option[];
  selectedOption: string | string[];
  onChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    selected: string | string[]
  ) => void;
  tooltip?: string;
  checkId?: string;
  ranking?: string | null;
}

interface IRadioButton {
  id: any;
  name: string;
  checked: any;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  titleDescription?: string;
  description?: string;
  ranking?: string | null;
  isMulti?: boolean;
}

export const InputRadioButton = ({
  id,
  name,
  checked,
  onChange,
  titleDescription,
  description,
  ranking,
  isMulti,
}: IRadioButton) => {
  const { getInputProps, getRadioProps, getRootProps } = useRadio({
    name,
    id,
    value: name,
    onChange,
    isChecked: checked,
  });

  const inputProps = getInputProps();
  const checkboxProps = getRadioProps();
  const rootProps = getRootProps();

  return (
    <Box
      as="li"
      role="option"
      id={`option-${id}`}
      tabIndex={-1} // foco via script
      aria-selected={checked}
      display="flex"
      alignItems="center"
      listStyleType="none"
      my="4px"
    >
      <EqualInputSelect
        id={id}
        name={name}
        checked={checked}
        checkboxProps={checkboxProps}
        inputProps={inputProps}
        rootProps={rootProps}
        titleDescription={titleDescription}
        description={description}
        ranking={ranking}
        isMulti={isMulti}
      />
    </Box>
  );
};

const InputSelect = ({
  isMulti,
  title,
  label,
  error,
  isSelected,
  height,
  widht,
  widhtMobile,
  disabled,
  isRequirement,
  options,
  selectedOption,
  onChange,
  isOportunity,
  readOnly,
  tooltip,
  checkId,
  ranking = null,
}: ISelect) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState<boolean>(false);
  const [check, setCheck] = React.useState("");
  const [dropdownPosition, setDropdownPosition] = React.useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const primaryColor = useColorModeValue(theme.primary, theme.primary);
  const textColor = useColorModeValue(theme.text, theme.text);
  const borderColor = useColorModeValue(theme.disable, theme.disable);

  const handleBorderColor = () => {
    if (disabled || readOnly) return `${theme.disable100}`;
    if (error) return `${theme.error}`;
    return `${theme.disable}`;
  };
  const handleBorderWidth = () => {
    if (disabled || readOnly) return `2px `;
    if (error) return `3px `;
    return `2px `;
  };

  const determineName = (option: Option) => option.name || option.nome;

  const determineId = (option: Option) =>
    option.id || option.idRelacionamentoComprador;

  const isChecked = (option: Option) => {
    const value = determineName(option);

    if (Array.isArray(selectedOption)) {
      return selectedOption.includes(value);
    }

    return selectedOption === value;
  };
  // Função para calcular a posição do dropdown considerando modais
  const calculateDropdownPosition = React.useCallback(() => {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();

    // Verifica se está dentro de um modal
    const modalElement = wrapperRef.current.closest(
      '[role="dialog"], .chakra-modal__content, .modal, [data-testid="modal"]'
    );

    let calculatedLeft = rect.left;
    let calculatedTop = rect.bottom + window.scrollY;
    const calculatedWidth = rect.width;

    if (modalElement) {
      // Se está dentro de um modal, usa coordenadas relativas ao viewport
      calculatedLeft = rect.left;
      calculatedTop = rect.bottom;

      // Verifica se o dropdown vai sair da tela horizontalmente
      const viewportWidth = window.innerWidth;
      if (calculatedLeft + calculatedWidth > viewportWidth) {
        calculatedLeft = viewportWidth - calculatedWidth - 10; // 10px de margem
      }

      // Verifica se o dropdown vai sair da tela verticalmente
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 170; // altura máxima do dropdown
      if (calculatedTop + dropdownHeight > viewportHeight) {
        calculatedTop = rect.top - dropdownHeight; // posiciona acima do input
      }
    }

    setDropdownPosition({
      top: calculatedTop,
      left: Math.max(0, calculatedLeft), // garante que não seja negativo
      width: calculatedWidth,
    });
  }, []);

  // Função personalizada para gerenciar cliques fora do componente e scroll
  React.useEffect(() => {
    if (!open) return;

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      // Verifica se o clique foi fora do wrapper E fora do dropdown
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }

    function handleScroll(event: Event) {
      const target = event.target as Element;

      // Verifica se o scroll está acontecendo dentro do dropdown
      if (
        dropdownRef.current &&
        (dropdownRef.current.contains(target) || dropdownRef.current === target)
      ) {
        // Se o scroll é dentro do dropdown, não fecha
        return;
      }

      // Se o scroll é fora do dropdown, fecha
      setOpen(false);
    }

    function handleResize() {
      calculateDropdownPosition();
    }

    // Adiciona um pequeno delay para evitar conflitos com o clique que abre o dropdown
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleResize);
      // Calcula a posição inicial
      calculateDropdownPosition();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [open, calculateDropdownPosition]);

  const handleOptionClick = (
    e: React.ChangeEvent<HTMLInputElement>,
    option: Option
  ) => {
    e.stopPropagation();
    const value = determineName(option);

    if (isMulti) {
      let updatedSelection: string[];

      if (Array.isArray(selectedOption)) {
        if (selectedOption.includes(value)) {
          updatedSelection = selectedOption.filter((v) => v !== value);
        } else {
          updatedSelection = [...selectedOption, value];
        }
      } else {
        updatedSelection = [value];
      }

      onChange(e, updatedSelection);
      // Não fecha o dropdown se for multi
      return;
    } else {
      // Caso isMulti seja false
      setCheck(determineId(option));
      onChange(e, value);
      setTimeout(() => {
        setOpen(false); // Fecha o dropdown apenas em seleção única
      }, 50);
    }
  };

  const handleOpenDropdown = () => {
    if (readOnly || disabled) return;
    calculateDropdownPosition();
    setOpen(!open);
  };

  const handleSelected = () => {
    if (isMulti) return isSelected?.length !== 0 ? "bold" : "normal";
    else return isSelected ? "bold" : "normal";
  };

  React.useEffect(() => {
    if (open && dropdownRef.current) {
      const firstItem =
        dropdownRef.current.querySelector<HTMLElement>('[role="option"]');
      firstItem?.focus();
    }
  }, [open]);

  // React.useEffect(() => {
  //   if (isSelected) {
  //     setOpen(false);
  //   }
  // }, [isSelected]);

  return (
    <Box ref={wrapperRef} width="100%" position="relative">
      <Flex alignItems="center">
        <Text fontSize="12px" color={textColor} ml="3px">
          {isRequirement && (
            <Text as="span" color={theme.error}>
              *
            </Text>
          )}
          {title}
        </Text>
        {tooltip && (
          <Tooltip text={tooltip}>
            <Styled.IconBsFillInfoCircleFill size="12px" />
          </Tooltip>
        )}
      </Flex>

      {/* Select Button */}
      <Flex
        tabIndex={0}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        h={isOportunity ? "34px" : "44px"}
        align="center"
        justify="space-between"
        px={3}
        cursor={readOnly || disabled ? "not-allowed" : "pointer"}
        bg={"white"}
        borderRadius="8px"
        borderWidth={handleBorderWidth()}
        borderColor={handleBorderColor()}
        _focus={
          !readOnly && !disabled
            ? {
                borderColor: primaryColor,
                borderWidth: "1.5px",
                boxShadow: `0 0 0 1px ${primaryColor}`,
              }
            : {}
        }
        onClick={handleOpenDropdown}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOpenDropdown();
          }
        }}
      >
        <Text
          color={readOnly || disabled ? theme.text50 : theme.text}
          fontWeight={handleSelected()}
          fontSize={
            Array.isArray(label) ? "12px" : label.length > 20 ? "12px" : "14px"
          }
          w={"50px"}
          isTruncated
          flex={1}
        >
          {Array.isArray(label) ? label.join(", ") : label}
        </Text>

        <Flex
          w={"fit-content"}
          direction="column"
          align="flex-end"
          justify="center"
        >
          <Icon as={IoIosArrowUp} color={primaryColor} fontSize="sm" />
          <Icon as={IoIosArrowDown} color={primaryColor} fontSize="sm" />
        </Flex>
      </Flex>

      {open && (
        <Portal>
          <Box
            ref={dropdownRef}
            position="fixed"
            maxHeight={height || "170px"}
            minHeight="50px"
            bg="white"
            borderRadius="8px"
            mt="1"
            border="1px solid"
            borderColor={borderColor}
            boxShadow="xl"
            zIndex={99999}
            display="flex"
            flexDirection="column"
            left={`${dropdownPosition.left}px`}
            top={`${dropdownPosition.top}px`}
            width={`${dropdownPosition.width}px`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Lista com scroll */}
            <Box
              as="ul"
              p="2"
              m="0"
              listStyleType="none"
              flex="1"
              overflowY="auto"
            >
              {options.map((option, index) => (
                <InputRadioButton
                  key={index}
                  id={index}
                  name={determineName(option)}
                  checked={isChecked(option) || option.name === selectedOption}
                  ranking={ranking}
                  onChange={(e) => handleOptionClick(e, option)}
                  isMulti={isMulti}
                />
              ))}
            </Box>

            {/* Botão fixo ao final */}
            {isMulti && Array.isArray(selectedOption) && selectedOption.length > 0 && (
              <Flex
                justify="center"
                align="center"
                borderTop="1px solid"
                borderColor={borderColor}
                p={"6px"}
                bg="white"
                cursor="pointer"
                _hover={{ bg: "gray.100" }}
                onClick={() => {
                  onChange({} as any, []); // limpa todas seleções
                }}
              >
                <Text w={"fit-content"} fontSize={"11px"} color={theme.error} fontWeight="bold">
                  Limpar seleção
                </Text>
              </Flex>
            )}
          </Box>
        </Portal>
      )}
    </Box>
  );
};
export default InputSelect;
